import React from 'react';
import includes from 'lodash/includes';

import { Box, List } from '@zillow/constellation-10';

import { CSTriggerSourceFromHeaderTitlesMap, HeaderTitles } from '../../constants';
import HeaderNavigationClickstreamEvents from '../../utils/clickstream';

import { HeaderButton } from '../HeaderButton';

// types
import type { Paths } from '@zg-rentals/paths-manifest';
import type { Displays } from '../../types';
import type { User } from '../../types';

export type Props = {
  displays: Displays;
  escapeAllLinks: boolean;
  link?: React.ComponentType<React.PropsWithChildren<{ to: string }>>;
  pathname: string;
  paths: Paths;
  useAbsolutePaths: boolean;
  userModel: User | null;
};

const SectionLeft = ({ displays, escapeAllLinks, link, pathname, paths, useAbsolutePaths, userModel }: Props) => {
  const {
    analyticsPage,
    analyticsOverview,
    billingPage,
    billingDue,
    leadsPage,
    maintenanceTrackerPage,
    propertiesPage,
    legacyZRM,
  } = paths;

  const { loggedIn } = userModel ?? {};

  const {
    displayAnalytics,
    displayMainBilling,
    displayMainLeads,
    displayMainMaintenanceTracker,
    displayMainMessages,
    displayMainMyProperties,
  } = displays;

  const handleLinkClick = (pageClickingTo: string) => {
    if (loggedIn && userModel) {
      const csLink = CSTriggerSourceFromHeaderTitlesMap[pageClickingTo];

      if (csLink) {
        HeaderNavigationClickstreamEvents.ClickNavigationLink({ user: userModel, link: csLink });
      } else {
        // eslint-disable-next-line
        console.error('ERROR@handleLinkClick: trigger source does not exist.', csLink);
      }
    }
  };

  return (
    <List.Root
      appearance="none"
      css={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', listStyleType: 'none' }}
    >
      {loggedIn && displayMainMyProperties && (
        <HeaderButton
          active={includes(pathname, paths.propertiesPage)}
          escape={escapeAllLinks}
          link={link}
          path={propertiesPage}
          title={HeaderTitles.myProperties}
          useAbsolutePaths={useAbsolutePaths}
          onClick={() => {
            handleLinkClick(HeaderTitles.myProperties);
          }}
        >
          {HeaderTitles.myProperties}
        </HeaderButton>
      )}

      {displayMainMessages && (
        <HeaderButton
          active={includes(pathname, 'inbox')}
          escape={true}
          link={link}
          path={legacyZRM.inbox}
          title={HeaderTitles.messages}
          useAbsolutePaths={useAbsolutePaths}
          onClick={() => handleLinkClick(HeaderTitles.messages)}
        >
          <Box css={{ display: 'flex', flexDirection: 'row' }}>{HeaderTitles.messages}</Box>
        </HeaderButton>
      )}

      {displayMainMaintenanceTracker && (
        <HeaderButton
          active={includes(pathname, 'rental-manager/maintenance-tasks')}
          onClick={() => handleLinkClick(HeaderTitles.maintenanceTracker)}
          escape={true}
          link={link}
          path={maintenanceTrackerPage}
          title={HeaderTitles.maintenanceTracker}
          useAbsolutePaths={useAbsolutePaths}
        >
          {HeaderTitles.maintenanceTracker}
        </HeaderButton>
      )}

      {displayMainLeads && (
        <HeaderButton
          active={includes(pathname, 'leads')}
          onClick={() => handleLinkClick(HeaderTitles.leads)}
          escape={true}
          link={link}
          path={leadsPage}
          title={HeaderTitles.leads}
          useAbsolutePaths={useAbsolutePaths}
        >
          {HeaderTitles.leads}
        </HeaderButton>
      )}

      {displayAnalytics && (
        <HeaderButton
          active={includes(pathname, paths.analyticsPage)}
          onClick={() => handleLinkClick(HeaderTitles.analytics)}
          escape={true}
          path={`${analyticsPage}/${analyticsOverview}`}
          title={HeaderTitles.analytics}
          useAbsolutePaths={useAbsolutePaths}
        >
          {HeaderTitles.analytics}
        </HeaderButton>
      )}

      {displayMainBilling && (
        <HeaderButton
          active={includes(pathname, billingPage)}
          onClick={() => handleLinkClick(HeaderTitles.billing)}
          escape={true}
          path={`${billingPage}/${billingDue}`}
          title={HeaderTitles.billing}
          useAbsolutePaths={useAbsolutePaths}
        >
          {HeaderTitles.billing}
        </HeaderButton>
      )}
    </List.Root>
  );
};

export default SectionLeft;
